import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { organizationsAPI, exportAPI } from '../services/api';
import { Building2, Plus, Search, Edit, Trash2, MoreHorizontal, Download } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

export default function ClientsPage() {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'sme',
    industry: '',
    country: '',
    contact_email: '',
    contact_phone: '',
    compliance_status: 'pending',
    risk_level: 'medium'
  });

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      const response = await organizationsAPI.getAll();
      setOrganizations(response.data);
    } catch (error) {
      toast.error('Failed to fetch organizations');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingOrg) {
        await organizationsAPI.update(editingOrg.id, formData);
        toast.success('Organization updated successfully');
      } else {
        await organizationsAPI.create(formData);
        toast.success('Organization created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchOrganizations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (org) => {
    setEditingOrg(org);
    setFormData({
      name: org.name,
      type: org.type,
      industry: org.industry || '',
      country: org.country || '',
      contact_email: org.contact_email || '',
      contact_phone: org.contact_phone || '',
      compliance_status: org.compliance_status,
      risk_level: org.risk_level
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this organization?')) return;
    try {
      await organizationsAPI.delete(id);
      toast.success('Organization deleted successfully');
      fetchOrganizations();
    } catch (error) {
      toast.error('Failed to delete organization');
    }
  };

  const resetForm = () => {
    setEditingOrg(null);
    setFormData({
      name: '',
      type: 'sme',
      industry: '',
      country: '',
      contact_email: '',
      contact_phone: '',
      compliance_status: 'pending',
      risk_level: 'medium'
    });
  };

  const filteredOrgs = organizations.filter(org =>
    org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    org.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (org.country && org.country.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="clients-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Clients</h1>
            <p className="text-muted-foreground">Manage your client organizations</p>
          </div>
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="add-client-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Client
          </Button>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search clients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="search-clients-input"
          />
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Organization</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>Compliance</TableHead>
                  <TableHead>Risk Level</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : filteredOrgs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="p-0 border-0">
                      <div className="py-12 px-6">
                        <div className="flex flex-col items-center justify-center text-center">
                          <div className="relative mb-6">
                            <div className="absolute inset-0 bg-teal-500/20 rounded-full blur-xl" />
                            <div className="relative flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-teal-500/10 to-teal-600/10 border border-teal-500/20">
                              <Building2 className="h-8 w-8 text-teal-600 dark:text-teal-400" />
                            </div>
                          </div>
                          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                            {searchTerm ? 'No matching clients' : 'No clients yet'}
                          </h3>
                          <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm mb-6">
                            {searchTerm 
                              ? 'Try adjusting your search to find what you\'re looking for.' 
                              : 'Add your first client organization to start managing their compliance journey.'}
                          </p>
                          {!searchTerm && (
                            <Button 
                              onClick={() => setDialogOpen(true)}
                              className="bg-slate-900 hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              Add Client
                            </Button>
                          )}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredOrgs.map((org) => (
                    <TableRow key={org.id} data-testid={`client-row-${org.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-muted rounded-md">
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div>
                            <p className="font-medium">{org.name}</p>
                            <p className="text-sm text-muted-foreground">{org.contact_email || 'No email'}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="capitalize">{org.type}</TableCell>
                      <TableCell>{org.country || '-'}</TableCell>
                      <TableCell>
                        <ComplianceBadge status={org.compliance_status} />
                      </TableCell>
                      <TableCell>
                        <RiskBadge level={org.risk_level} />
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`client-actions-${org.id}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEdit(org)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={async () => {
                              try {
                                toast.info('Preparing export... This may take a moment.');
                                const res = await exportAPI.organizationFullExport(org.id);
                                const url = window.URL.createObjectURL(new Blob([res.data]));
                                const a = document.createElement('a'); a.href = url;
                                a.download = res.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') || `${org.name}_Export.zip`;
                                document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
                                toast.success('Data exported successfully');
                              } catch { toast.error('Export failed'); }
                            }} data-testid={`export-org-data-${org.id}`}>
                              <Download className="h-4 w-4 mr-2" />
                              Export All Data
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDelete(org.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingOrg ? 'Edit Organization' : 'Add New Organization'}</DialogTitle>
              <DialogDescription>
                {editingOrg ? 'Update organization details' : 'Enter the details for the new client organization'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Organization Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      required
                      data-testid="org-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="type">Type *</Label>
                    <Select
                      value={formData.type}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, type: value }))}
                    >
                      <SelectTrigger data-testid="org-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="group">Group of Companies</SelectItem>
                        <SelectItem value="sme">SME</SelectItem>
                        <SelectItem value="ngo">NGO</SelectItem>
                        <SelectItem value="government">Government</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="industry">Industry</Label>
                    <Input
                      id="industry"
                      value={formData.industry}
                      onChange={(e) => setFormData(prev => ({ ...prev, industry: e.target.value }))}
                      data-testid="org-industry-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="country">Country</Label>
                    <Input
                      id="country"
                      value={formData.country}
                      onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                      data-testid="org-country-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contact_email">Contact Email</Label>
                    <Input
                      id="contact_email"
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
                      data-testid="org-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_phone">Contact Phone</Label>
                    <Input
                      id="contact_phone"
                      value={formData.contact_phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, contact_phone: e.target.value }))}
                      data-testid="org-phone-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="compliance_status">Compliance Status</Label>
                    <Select
                      value={formData.compliance_status}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, compliance_status: value }))}
                    >
                      <SelectTrigger data-testid="org-compliance-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="compliant">Compliant</SelectItem>
                        <SelectItem value="partial">Partial</SelectItem>
                        <SelectItem value="non_compliant">Non-Compliant</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="risk_level">Risk Level</Label>
                    <Select
                      value={formData.risk_level}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, risk_level: value }))}
                    >
                      <SelectTrigger data-testid="org-risk-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-org-btn">
                  {editingOrg ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

function ComplianceBadge({ status }) {
  const variants = {
    compliant: 'success',
    partial: 'warning',
    non_compliant: 'danger',
    pending: 'secondary'
  };
  const labels = {
    compliant: 'Compliant',
    partial: 'Partial',
    non_compliant: 'Non-Compliant',
    pending: 'Pending'
  };
  return (
    <Badge variant={variants[status] || 'secondary'}>
      {labels[status] || status}
    </Badge>
  );
}

function RiskBadge({ level }) {
  const variants = {
    low: 'success',
    medium: 'warning',
    high: 'danger',
    critical: 'destructive'
  };
  return (
    <Badge variant={variants[level] || 'secondary'} className="capitalize">
      {level}
    </Badge>
  );
}
